import { useEffect, useRef } from "react";
import axios from "axios";
import { TIMER_KEYS } from "@/constants/timers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Text/timer fields that should be debounced (600ms) instead of patched immediately.
const DEBOUNCED_KEYS = new Set(["ballpark", ...TIMER_KEYS]);

/**
 * Syncs a settings object to the server whenever any value changes.
 *
 * Replaces the 24 individual useEffect hooks in PvPSetup. Compares against
 * the previous settings ref on every render and only PATCHes the changed keys.
 * String/timer fields are debounced 600ms; all other fields are immediate.
 *
 * Usage:
 *   useSettingsSync(gameId, settings);
 */
export function useSettingsSync(gameId, settings) {
  const prevRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = settings;

    if (!gameId || !prev) return; // no gameId yet, or first render — skip PATCH

    const immediate = {};
    const debounced = {};
    for (const [k, v] of Object.entries(settings)) {
      if (prev[k] !== v) {
        (DEBOUNCED_KEYS.has(k) ? debounced : immediate)[k] = v;
      }
    }

    if (Object.keys(immediate).length) {
      axios.patch(`${API}/games/${gameId}/settings`, immediate).catch(() => {});
    }
    if (Object.keys(debounced).length) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        axios.patch(`${API}/games/${gameId}/settings`, debounced).catch(() => {});
      }, 600);
    }
  }); // No deps array: runs after every render; ref-based diff prevents redundant PATCHs.

  // Cancel any pending debounced PATCH on unmount.
  useEffect(() => () => clearTimeout(debounceRef.current), []);
}
